import { supabase } from "@/integrations/supabase/client";

let lastClickedElement = '';
let isInitialized = false;

function getClickLabel(target: EventTarget | null): string {
  if (!target || !(target instanceof HTMLElement)) return '';
  
  // Try to find the closest button or link
  const clickable = target.closest('button, a, [role="button"], input[type="submit"]');
  if (!clickable) return target.textContent?.trim().slice(0, 100) || target.tagName;
  
  const text = clickable.textContent?.trim().slice(0, 100);
  const ariaLabel = clickable.getAttribute('aria-label');
  const title = clickable.getAttribute('title');
  
  return ariaLabel || title || text || clickable.tagName;
}

function trackClicks() {
  document.addEventListener('click', (e) => {
    const label = getClickLabel(e.target);
    if (label) {
      lastClickedElement = label;
    }
  }, true);
}

async function getUserInfo(): Promise<{ isLoggedIn: boolean; firstName?: string; lastName?: string; email?: string }> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return { isLoggedIn: false };
    
    const meta = session.user.user_metadata;
    return {
      isLoggedIn: true,
      firstName: meta?.first_name || '',
      lastName: meta?.last_name || '',
      email: session.user.email || '',
    };
  } catch {
    return { isLoggedIn: false };
  }
}

async function createErrorCase(errorMessage: string, source: string) {
  try {
    const userInfo = await getUserInfo();
    const pageUrl = window.location.href;
    
    const description = [
      `**Page :** ${pageUrl}`,
      `**Source :** ${source}`,
      `**Utilisateur connecté :** ${userInfo.isLoggedIn ? 'Oui' : 'Non'}`,
      userInfo.isLoggedIn ? `**Nom :** ${userInfo.firstName} ${userInfo.lastName}` : '',
      userInfo.isLoggedIn ? `**Email :** ${userInfo.email}` : '',
      `**Dernier élément cliqué :** ${lastClickedElement || 'Aucun'}`,
      `**Message d'erreur :** ${errorMessage}`,
      `**Date :** ${new Date().toLocaleString('fr-FR')}`,
      `**User Agent :** ${navigator.userAgent}`,
    ].filter(Boolean).join('\n');

    await supabase.from('cases').insert({
      subject: `[Error Log] ${errorMessage.slice(0, 120)}`,
      description,
      origin: 'error_log_system',
      priority: 'high',
      status: 'new',
      client_name: userInfo.isLoggedIn ? `${userInfo.firstName} ${userInfo.lastName}`.trim() : 'Visiteur anonyme',
      client_email: userInfo.email || null,
    });
  } catch (e) {
    // Silent fail to avoid infinite loops
    console.warn('[ErrorTracking] Failed to create case:', e);
  }
}

// Debounce to avoid flooding the DB with duplicate errors
const recentErrors = new Set<string>();

function deduplicatedReport(message: string, source: string) {
  const key = `${message}::${window.location.pathname}`;
  if (recentErrors.has(key)) return;
  recentErrors.add(key);
  setTimeout(() => recentErrors.delete(key), 60_000);
  createErrorCase(message, source);
}

// Public function to manually report caught errors from anywhere in the app
export function reportError(error: unknown, source: string = 'manual') {
  const message = error instanceof Error ? error.message : String(error);
  if (!message || message.includes('ResizeObserver')) return;
  deduplicatedReport(message, source);
}

export function initErrorTracking() {
  if (isInitialized) return;
  isInitialized = true;

  trackClicks();

  window.addEventListener('error', (event) => {
    const message = event.message || 'Unknown error';
    if (message.includes('ResizeObserver') || message.includes('Script error')) return;
    deduplicatedReport(message, 'window.onerror');
  });

  window.addEventListener('unhandledrejection', (event) => {
    const message = event.reason?.message || event.reason?.toString() || 'Unhandled promise rejection';
    if (message.includes('ResizeObserver')) return;
    deduplicatedReport(message, 'unhandledrejection');
  });

  // Intercept console.error to catch errors from try/catch blocks and libraries
  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    originalConsoleError.apply(console, args);
    const message = args.map(a => (a instanceof Error ? a.message : String(a))).join(' ');
    if (message.includes('ResizeObserver') || message.includes('[ErrorTracking]')) return;
    deduplicatedReport(message, 'console.error');
  };
}
